import React, { useRef } from 'react';
import { Info, Lock } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

export interface KpiItem {
  label: string;
  value: string;
  /** Shown instead of value when the card is too narrow to display the full value. */
  compactValue?: string;
  sub: string;
  valueClass?: string;
  /** Tooltip content — accepts a string or rich JSX. */
  tip: React.ReactNode;
  /** Period label shown below the title (e.g. "Last Quarter"). */
  period?: string | null;
  /** When true, shows a green "Live" badge instead of the period label. */
  live?: boolean;
  /** Grays out the cell and shows a lock icon; disables onClick. */
  disabled?: boolean;
  /** Makes the cell clickable with a hover highlight. */
  onClick?: () => void;
  /** Optional breakdown lines shown below the sub text. */
  details?: Array<{ label: string; text: string }>;
  /** Small inline badges rendered next to the label. */
  labelBadges?: Array<{ text: string; className?: string }>;
}

interface KpiGroupCardProps {
  items: KpiItem[];
}

export const KpiGroupCard = ({ items }: KpiGroupCardProps) => {
  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <div ref={containerRef} className="rounded-lg border bg-card shadow-sm flex overflow-hidden">
      {items.map((item, i) => (
        <React.Fragment key={item.label}>
          {i > 0 && (
            <div className="self-center h-8 w-px bg-border/70 shrink-0" />
          )}
          <div
            className={`flex-1 min-w-[120px] px-3 py-2.5 ${item.onClick && !item.disabled ? 'cursor-pointer hover:bg-muted/40 transition-colors' : ''} ${item.disabled ? 'opacity-50' : ''}`}
            onClick={item.disabled ? undefined : item.onClick}
            title={item.onClick && !item.disabled ? `Click to filter by ${item.label.toLowerCase()}` : undefined}
          >
            <div className="flex items-start justify-between gap-1 min-h-[2.5rem]">
              <div className="flex items-start gap-1 min-w-0">
                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide leading-tight line-clamp-3">
                  {item.label}
                </p>
                {item.labelBadges?.map(b => (
                  <span key={b.text} className={`text-[9px] font-semibold px-1 py-px rounded whitespace-nowrap shrink-0 ${b.className ?? 'bg-muted text-muted-foreground'}`}>
                    {b.text}
                  </span>
                ))}
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  {item.disabled
                    ? <Lock className="h-3 w-3 text-muted-foreground/40 cursor-default shrink-0" />
                    : <Info className="h-3 w-3 text-muted-foreground/30 hover:text-muted-foreground cursor-default shrink-0" />
                  }
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs text-xs">{item.tip}</TooltipContent>
              </Tooltip>
            </div>

            <div className="h-3.5 flex items-center mt-0.5">
              {item.live ? (
                <span className="flex items-center gap-0.5 text-[9px] font-medium text-emerald-600">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 inline-block" />
                  Live
                </span>
              ) : item.period ? (
                <span className="text-[9px] text-muted-foreground truncate">{item.period}</span>
              ) : null}
            </div>

            <p className={`text-base font-bold tabular-nums leading-tight truncate ${item.valueClass ?? ''}`}>
              {item.compactValue ?? item.value}
            </p>
            <p className="text-[10px] text-muted-foreground mt-0.5">{item.sub}</p>
            {item.details && item.details.length > 0 && (
              <div className="mt-1 space-y-0.5">
                {item.details.map(d => (
                  <div key={d.label} className="flex items-center gap-1">
                    <span className="text-[9px] text-muted-foreground/70 font-medium">{d.label}:</span>
                    <span className="text-[9px] text-muted-foreground truncate">{d.text}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </React.Fragment>
      ))}
    </div>
  );
};
